import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Ellipsis } from '@/components/ui/ellipsis';
import { AssigneesBadge } from '@/components/ui/assignees-badge';
import { getGeographyFlag, getGeographyLabel } from '@/shared/geography';
import { Search, Sparkles } from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { type SourceItem } from './SourceList';

export type NewsSourceListProps = {
  sources: SourceItem[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onBulkToggle?: (sourceIds: string[], subscribe: boolean) => void;
  onAssignSource?: (sourceId: string, memberIds: string[]) => void;
};

export const NewsSourceList: FC<NewsSourceListProps> = ({
  sources,
  selectedIds,
  onToggle,
  onBulkToggle,
  onAssignSource,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSources = useMemo(() => {
    if (!searchQuery.trim()) return sources;
    const q = searchQuery.toLowerCase();
    return sources.filter(
      s =>
        s.title.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q) ||
        getGeographyLabel(s.geography).toLowerCase().includes(q)
    );
  }, [sources, searchQuery]);

  const totalSelectedCount = filteredSources.filter(s =>
    selectedIds.has(s.id)
  ).length;
  const allSelected =
    filteredSources.length > 0 && totalSelectedCount === filteredSources.length;

  const toggleSelectAll = () => {
    const subscribe = !allSelected;
    const idsToToggle = filteredSources
      .filter(s => (subscribe ? !selectedIds.has(s.id) : selectedIds.has(s.id)))
      .map(s => s.id);

    if (idsToToggle.length === 0) return;

    if (onBulkToggle) {
      onBulkToggle(idsToToggle, subscribe);
    } else {
      for (const id of idsToToggle) onToggle(id);
    }
  };

  return (
    <>
      {/* Search bar + Select all */}
      <div className="px-6 pt-4 pb-3 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="relative w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500" />
            <Input
              placeholder="Search news"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex-1 flex items-center justify-end gap-2">
            <span className="text-xs font-medium text-neutral-900">
              Select all
            </span>
            <Switch checked={allSelected} onCheckedChange={toggleSelectAll} />
          </div>
        </div>
      </div>

      {/* Source cards */}
      <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
        <div className="flex flex-col gap-3">
          {filteredSources.map(source => {
            const flagUrl = getGeographyFlag(source.geography);
            const countryLabel = getGeographyLabel(source.geography);
            return (
              <label
                key={source.id}
                className="flex items-start gap-4 bg-white rounded-2xl border border-neutral-200 p-4 cursor-pointer hover:bg-neutral-50 transition-colors"
              >
                {/* Country flag */}
                <div className="shrink-0 w-10 h-10 rounded-lg bg-neutral-100 border border-neutral-200 flex items-center justify-center overflow-hidden">
                  {flagUrl ? (
                    <img
                      src={flagUrl}
                      alt={countryLabel}
                      className="w-6 h-6 object-contain"
                    />
                  ) : (
                    <span className="text-base leading-none">🌐</span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-neutral-900">
                      {source.title}
                    </span>
                    {source.geography &&
                      source.geography !== 'world' &&
                      source.geography !== 'unknown' && (
                        <span className="text-xs text-neutral-400 shrink-0">
                          {countryLabel}
                        </span>
                      )}
                  </div>
                  {source.description && (
                    <p className="text-sm text-neutral-500 line-clamp-3">
                      {source.description}
                    </p>
                  )}
                  {source.reasoning && (
                    <div className="flex items-center gap-1.5 mt-1 min-w-0">
                      <Sparkles className="size-3.5 text-magenta-500 shrink-0" />
                      <Ellipsis
                        hasTooltip
                        className="text-xs text-neutral-500 italic"
                      >
                        {source.reasoning}
                      </Ellipsis>
                    </div>
                  )}
                </div>

                {/* Assignment + Checkbox */}
                {selectedIds.has(source.id) && onAssignSource && (
                  <div onClick={e => e.stopPropagation()}>
                    <AssigneesBadge
                      assignedMemberIds={source.assignedMemberIds ?? []}
                      onAssign={memberIds =>
                        onAssignSource(source.id, memberIds)
                      }
                    />
                  </div>
                )}
                <Checkbox
                  checked={selectedIds.has(source.id)}
                  onCheckedChange={() => onToggle(source.id)}
                  className="shrink-0 mt-1"
                />
              </label>
            );
          })}
          {filteredSources.length === 0 && (
            <div className="border rounded-xl p-6 text-center text-sm text-muted-foreground">
              No news sources match the search.
            </div>
          )}
        </div>
      </div>
    </>
  );
};
